import Button from '@/components/UI/Button';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import Icon from '@/components/UI/Icon';
import SearchSelect from '@/components/UI/SearchSelect/SearchSelect';
import type { Webhook, WebhookEventType, WebhookUpdate } from '@/model/WebhookConfiguration.model';
import { _isNotBlank } from '@/littledash';
import { successToast } from '@/helpers';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { RouteService } from '@/support/RouteService';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { FC } from 'react';
import { useCallback } from 'react';
import { FormProvider, useController, useFieldArray, useForm, useWatch } from 'react-hook-form@latest';
import { FieldArrayError, FormInput } from '../FormInput';
import type { WebhookFormValues } from '../Webhook.utils';
import {
  arraysEqual,
  headerKeyRules,
  headerValueRules,
  nameValidator,
  URL_MAX_LENGTH,
  urlValidator,
  useDebouncedUrlUnique,
  useRegisterHeader,
  useWebhookEvents,
} from '../Webhook.utils';
import styles from '../Webhook.module.scss';

interface EditWebhookProps {
  closeModal: () => void;
  onEditWebhook: () => void;
  webhook: Webhook;
  eventTypes: Array<WebhookEventType>;
}

/**
 * Modal for editing an existing webhook configuration. Pre-populates name, URL, header keys,
 * and events from the current webhook. Only sends changed fields via PATCH.
 */
export const EditWebhook: FC<EditWebhookProps> = ({ onEditWebhook, closeModal, webhook, eventTypes }) => {
  const { invoke: updateWebhook, loading: isSubmitting } = useApiHook({
    endpoint: 'PATCH /api/v1/webhooks/{webhookId}',
    invokeOnInit: false,
    options: { onError: { toast: true, throw: true, slug: 'update-webhook' } },
  });

  const formMethods = useForm<WebhookFormValues>({
    defaultValues: {
      name: webhook.name,
      url: webhook.url,
      headers:
        webhook.header_keys.length > 0
          ? webhook.header_keys.map((key) => ({ key, value: '' }))
          : [{ key: '', value: '' }],
      events: [...webhook.events],
    },
    mode: 'onChange',
  });

  const { register, handleSubmit, setValue, control, formState, getValues, trigger } = formMethods;

  const debouncedUrlUnique = useDebouncedUrlUnique();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'headers',
    rules: {
      validate: (headers) => {
        const originalKeys = new Set(webhook.header_keys);
        const empty = headers.filter((h) => !_isNotBlank(h.key) && !_isNotBlank(h.value));
        const partial = headers.filter((h) => {
          if (originalKeys.has(h.key) && !_isNotBlank(h.value)) {
            return false;
          }
          return _isNotBlank(h.key) !== _isNotBlank(h.value);
        });
        if (partial.length > 0) {
          return 'Headers must have both a key and a value';
        }
        if (empty.length > 0 && headers.length > 1) {
          return 'Remove empty header rows or fill them in';
        }
        return true;
      },
    },
  });

  const {
    field: { value: events },
  } = useController({
    control,
    name: 'events',
    rules: {
      validate: (value) => (value?.length ?? 0) > 0 || 'At least one event is required',
    },
  });

  const watchedName = useWatch({ control, name: 'name' });
  const watchedUrl = useWatch({ control, name: 'url' });
  const watchedHeaders = useWatch({ control, name: 'headers' });

  const isNameDirty = watchedName?.trim() !== webhook.name;
  const isUrlDirty = watchedUrl !== webhook.url;
  const isEventsDirty = !arraysEqual(events ?? [], webhook.events);
  const hasCompleteHeaders = watchedHeaders.some((h) => _isNotBlank(h.key) && _isNotBlank(h.value));
  const isHeadersDirty = (() => {
    const currentKeys = watchedHeaders.reduce<Array<string>>((acc, h) => {
      if (_isNotBlank(h.key)) {
        acc.push(h.key);
      }
      return acc;
    }, []);
    return !arraysEqual(currentKeys, webhook.header_keys) || hasCompleteHeaders;
  })();
  const isDirty = isNameDirty || isUrlDirty || isEventsDirty || isHeadersDirty;

  const isSubmitDisabled = !formState.isValid || !isDirty || formState.isValidating || isSubmitting;

  const { webhookEventSections, handleSetSelected, handleOnSelect, handleSelectAll } = useWebhookEvents(
    eventTypes,
    events,
    setValue
  );

  const registerHeader = useRegisterHeader(register, trigger);

  const handleAddHeader = useCallback(() => {
    append({ key: '', value: '' });
  }, [append]);

  const onSubmit = useCallback(
    async (formData: WebhookFormValues) => {
      try {
        const body: WebhookUpdate = {};

        if (formData.name.trim() !== webhook.name) {
          body.name = formData.name.trim();
        }

        if (formData.url !== webhook.url) {
          body.url = formData.url;
        }

        if (!arraysEqual(formData.events, webhook.events)) {
          body.events = formData.events as WebhookUpdate['events'];
        }

        const formKeys = formData.headers.reduce<Array<string>>((acc, h) => {
          if (_isNotBlank(h.key)) {
            acc.push(h.key);
          }
          return acc;
        }, []);
        const hasNewCompleteHeaders = formData.headers.some((h) => _isNotBlank(h.key) && _isNotBlank(h.value));
        const headersDirty = !arraysEqual(formKeys, webhook.header_keys) || hasNewCompleteHeaders;

        if (headersDirty) {
          const headers: Record<string, string | null> = {};
          formData.headers.forEach(({ key, value }) => {
            if (_isNotBlank(key)) {
              headers[key.trim()] = _isNotBlank(value) ? value.trim() : null;
            }
          });
          body.headers = headers;
        }

        await updateWebhook({ path: { webhookId: webhook.api_id }, body } as Record<string, unknown>);
        onEditWebhook();
        closeModal();
        successToast('Webhook updated');
      } catch {
        // Error already handled by useApiHook (toast + exception capture)
      }
    },
    [updateWebhook, onEditWebhook, closeModal, webhook]
  );

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title="Edit webhook"
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText={
          <>
            Update the webhook configuration. See the{' '}
            <a
              href={RouteService.help('developer_platform')}
              target="_blank"
              rel="noopener noreferrer"
              className="link blue"
              data-testid="api-docs-link"
            >
              API docs
            </a>{' '}
            for details.
          </>
        }
        subTextClasses="f6 lh-copy mt2 measure-wide"
      />
      <FormProvider {...formMethods}>
        <div className={`pv3 ph2 overflow-y-auto ${styles.modalBody}`}>
          <div className={`mb3 ph2 ${styles.formInput}`}>
            <FormField name="name" label="Name" required testPrefix="webhook-name">
              <FormInput
                {...register('name', nameValidator)}
                name="name"
                type="text"
                placeholder="e.g. Production Event Notifications"
                maxLength={255}
                data-testid="webhook-name-input"
              />
            </FormField>
          </div>

          <div className={`mb3 ph2 ${styles.formInput}`}>
            <FormField name="url" label="HTTPS endpoint" required testPrefix="webhook-url">
              <div className={styles.urlRow}>
                <FormInput
                  {...register('url', {
                    required: 'URL is required',
                    validate: {
                      format: urlValidator,
                      unique: (value) => (value === webhook.url ? true : debouncedUrlUnique(value)),
                    },
                  })}
                  name="url"
                  type="url"
                  placeholder="https://your-server.com/webhook"
                  maxLength={URL_MAX_LENGTH}
                  data-testid="webhook-url-input"
                />
                <div className={styles.headerSpacer} />
              </div>
            </FormField>
          </div>

          <div className="mb3 ph2">
            <FormField name="headers" label="Authentication headers" testPrefix="webhook-headers">
              <div className={`mb1 ${styles.headerLabels}`}>
                <div className={styles.headerLabel}>
                  <label className="f7 dark-gray db">Header key</label>
                </div>
                <div className={styles.headerLabel}>
                  <label className="f7 dark-gray db">Header value</label>
                </div>
                <div className={styles.headerSpacer} />
              </div>
              {fields.map((field, index) => (
                <div key={field.id} className={`mb2 ${styles.headerRow}`} data-testid={`header-row-${index}`}>
                  <div className={styles.headerField}>
                    <FormInput
                      {...registerHeader(`headers.${index}.key`, headerKeyRules(index, getValues))}
                      name={`headers.${index}.key`}
                      type="text"
                      placeholder="X-API-Key"
                      data-testid={`header-key-${index}`}
                    />
                  </div>
                  <div className={styles.headerField}>
                    <FormInput
                      {...registerHeader(`headers.${index}.value`, headerValueRules(index))}
                      name={`headers.${index}.value`}
                      type="password"
                      placeholder={
                        index < webhook.header_keys.length && watchedHeaders[index]?.key === webhook.header_keys[index]
                          ? '✳︎'.repeat(10)
                          : 'Value'
                      }
                      data-testid={`header-value-${index}`}
                    />
                  </div>
                  {index > 0 ? (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className={styles.removeHeader}
                      data-testid={`remove-header-${index}`}
                      aria-label={`Remove header ${index + 1}`}
                    >
                      <Icon icon="close" width="10" height="10" className="dark-gray" />
                    </button>
                  ) : (
                    <div className={styles.headerSpacer} />
                  )}
                </div>
              ))}
              <Button
                onClick={handleAddHeader}
                className="br-pill f6"
                icon="add_new"
                paleBlue
                testId="add-header-button"
              >
                <span>Add header</span>
              </Button>
              <FieldArrayError name="headers" />
            </FormField>
          </div>

          <div className="mb3 ph2">
            <FormField name="events" label="Events" required testPrefix="webhook-events">
              <div className={styles.searchSelectContainer}>
                <SearchSelect
                  sections={webhookEventSections}
                  selected={events ?? []}
                  setSelected={handleSetSelected}
                  search={true}
                  enableSelectAll={true}
                  customSearchPlaceholderText="Search events"
                  testPrefix="webhook-events"
                  overflowHeight={240}
                  onSelect={handleOnSelect}
                  onSelectAll={handleSelectAll}
                />
              </div>
            </FormField>
          </div>
        </div>
      </FormProvider>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: isSubmitDisabled,
          loading: isSubmitting,
        }}
      />
    </ModalContainer>
  );
};
